import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Upload, 
  message, 
  Space, 
  Image,
  Grid,
  Card,
  Typography,
  Popconfirm
} from 'antd';
import { 
  UploadOutlined, 
  EditOutlined, 
  DeleteOutlined,
  PlusOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS } from './config';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.LIST_BLOGS);
      if (Array.isArray(res.data)) {
        setBlogs(res.data);
      } else {
        setBlogs([]);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      message.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const openModal = (edit = false, blog = null) => {
    setIsEditing(edit);
    setCurrentBlog(blog);
    setFileList([]);
    form.resetFields();
    if (edit && blog) {
      form.setFieldsValue(blog);
    }
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.post(API_ENDPOINTS.DELETE_BLOG, {
        action: 'delete',
        id,
      });
      message.success('Blog deleted successfully');
      fetchBlogs();
    } catch (err) {
      console.error('Error deleting blog:', err);
      message.error('Failed to delete blog');
    }
  };

  const handleFormSubmit = async (values) => {
    const formData = new FormData();
    formData.append('action', isEditing ? 'edit' : 'add');

    Object.entries(values).forEach(([k, v]) => formData.append(k, v));
    if (isEditing) formData.append('id', currentBlog.id);

    if (fileList.length > 0) {
      formData.append('blog_image', fileList[0].originFileObj);
    }

    try {
      setLoading(true);
      const res = await axios.post(API_ENDPOINTS.ADD_OR_EDIT_BLOG, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success(res.data.message);
      setModalVisible(false);
      fetchBlogs();
    } catch (err) {
      console.error('Operation failed:', err);
      message.error('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
    }
    return isImage;
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'blog_image',
      key: 'blog_image',
      width: 100,
      responsive: ['md'],
      render: (image) => (
        image ? (
          <Image
            width={80}
            height={60}
            src={image}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            alt="Blog thumbnail"
            preview={{
              src: image,
            }}
          />
        ) : (
          <div style={{
            width: 80,
            height: 60,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4
          }}>
            <Text type="secondary">No Image</Text>
          </div>
        )
      ),
    },
    {
      title: 'Name',
      dataIndex: 'blog_name',
      key: 'blog_name',
      width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Telugu Content',
      dataIndex: 'blog_content_telugu',
      key: 'blog_content_telugu',
      responsive: ['lg'],
      render: (text) => text ? (
        <Text ellipsis={{ tooltip: text }}>
          {text.substring(0, 50) + (text.length > 50 ? '...' : '')}
        </Text>
      ) : <Text type="secondary">Empty</Text>,
    },
    {
      title: 'English Content',
      dataIndex: 'blog_content_english',
      key: 'blog_content_english',
      responsive: ['lg'],
      render: (text) => text ? (
        <Text ellipsis={{ tooltip: text }}>
          {text.substring(0, 50) + (text.length > 50 ? '...' : '')}
        </Text>
      ) : <Text type="secondary">Empty</Text>,
    },
    {
      title: 'URL',
      dataIndex: 'blog_url',
      key: 'blog_url',
      responsive: ['xl'],
      render: (url) => url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Text ellipsis style={{ maxWidth: 150 }}>
            {url.replace(/^https?:\/\//, '')}
          </Text>
        </a>
      ) : <Text type="secondary">None</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: screens.md ? false : 'right',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => openModal(true, record)}
            size="small"
          />
          <Popconfirm
            title="Are you sure to delete this blog?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            placement="topRight"
          >
            <Button 
              icon={<DeleteOutlined />} 
              danger 
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? 12 : 24 }}>
      <Card
        title={<Title level={4}>Blog Management</Title>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal(false)}
          >
            Add New Blog
          </Button>
        }
        bordered={false}
      >
        <Table 
          dataSource={blogs} 
          columns={columns} 
          rowKey="id"
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} blogs`,
            responsive: true
          }}
          size={screens.xs ? 'small' : 'middle'}
          sticky
        />
      </Card>

      <Modal
        title={isEditing ? 'Edit Blog' : 'Add Blog'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={screens.xs ? '90%' : 800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={currentBlog || { is_exists: 1 }}
          onFinish={handleFormSubmit}
        >
          <Form.Item
            label="Blog Name"
            name="blog_name"
            rules={[{ required: true, message: 'Please input blog name!' }]}
          >
            <Input placeholder="Enter blog name" />
          </Form.Item>

          <Form.Item label="Content (Telugu)" name="blog_content_telugu">
            <Input.TextArea rows={4} placeholder="Enter content in Telugu" />
          </Form.Item>

          <Form.Item label="Content (English)" name="blog_content_english">
            <Input.TextArea rows={4} placeholder="Enter content in English" />
          </Form.Item>

          <Form.Item label="Blog URL" name="blog_url">
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item label="Blog Image">
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              accept="image/*"
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {currentBlog?.blog_image && !fileList.length && (
              <Image
                width={100}
                src={currentBlog.blog_image}
                alt="Current blog image"
                style={{ marginTop: 10, borderRadius: 4 }}
              />
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={loading}
              >
                {isEditing ? 'Update' : 'Add'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogManager;